import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { format } from 'date-fns';
import { Search, CalendarDays, Users, MapPin } from 'lucide-react';
import { getCities } from '../lib/api';

const CITIES = [
  { id: 'cape-coral', name: 'Cape Coral', slug: 'cape-coral' },
  { id: 'fort-myers', name: 'Fort Myers', slug: 'fort-myers' },
  { id: 'fort-myers-beach', name: 'Fort Myers Beach', slug: 'fort-myers-beach' },
  { id: 'sanibel', name: 'Sanibel', slug: 'sanibel' },
  { id: 'captiva', name: 'Captiva', slug: 'captiva' },
  { id: 'bonita-springs', name: 'Bonita Springs', slug: 'bonita-springs' },
  { id: 'estero', name: 'Estero', slug: 'estero' },
  { id: 'naples', name: 'Naples', slug: 'naples' },
  { id: 'marco-island', name: 'Marco Island', slug: 'marco-island' },
];

export default function SearchBar({ 
  variant = 'default', 
  initialValues = {},
  onSearch 
}) {
  const navigate = useNavigate();
  const [city, setCity] = useState(initialValues.city || '');
  const [checkIn, setCheckIn] = useState(initialValues.checkIn || null);
  const [checkOut, setCheckOut] = useState(initialValues.checkOut || null);
  const [guests, setGuests] = useState(initialValues.guests || '');
  const [cities, setCities] = useState(CITIES);

  useEffect(() => {
    const loadCities = async () => {
      try {
        const response = await getCities();
        if (response.data && response.data.length > 0) {
          setCities(response.data);
        }
      } catch (error) {
        console.error('Failed to load cities:', error);
      }
    };
    loadCities();
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    
    if (city) params.set('city', city);
    if (checkIn) params.set('check_in', format(checkIn, 'yyyy-MM-dd'));
    if (checkOut) params.set('check_out', format(checkOut, 'yyyy-MM-dd'));
    if (guests) params.set('guests', guests);

    if (onSearch) {
      onSearch({ city, checkIn, checkOut, guests });
    } else {
      navigate(`/search?${params.toString()}`);
    }
  };

  const isHero = variant === 'hero';

  return (
    <div 
      className={`
        ${isHero 
          ? 'bg-white/95 backdrop-blur-md rounded-2xl p-6 shadow-floating' 
          : 'bg-white rounded-xl p-4 border border-slate-200 shadow-sm'
        }
      `}
      data-testid="search-bar"
    >
      <div className={`grid gap-4 ${isHero ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
        {/* City Select */}
        <div className="space-y-2">
          <Label className="text-slate-600 text-sm flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            Destination
          </Label>
          <Select value={city} onValueChange={setCity}>
            <SelectTrigger className="input-default" data-testid="search-city">
              <SelectValue placeholder="Where to?" />
            </SelectTrigger>
            <SelectContent data-testid="search-city-options">
              {cities.map((c) => (
                <SelectItem key={c.id} value={c.id} data-testid={`search-city-option-${c.id}`}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Check-in Date */}
        <div className="space-y-2">
          <Label className="text-slate-600 text-sm flex items-center gap-1">
            <CalendarDays className="w-4 h-4" />
            Check In
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="input-default w-full justify-start text-left font-normal"
                data-testid="search-checkin"
              >
                {checkIn ? format(checkIn, 'MMM d, yyyy') : 'Add date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkIn}
                onSelect={setCheckIn}
                disabled={(date) => date < new Date()}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Check-out Date */}
        <div className="space-y-2">
          <Label className="text-slate-600 text-sm flex items-center gap-1">
            <CalendarDays className="w-4 h-4" />
            Check Out
          </Label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="input-default w-full justify-start text-left font-normal"
                data-testid="search-checkout"
              >
                {checkOut ? format(checkOut, 'MMM d, yyyy') : 'Add date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={checkOut}
                onSelect={setCheckOut}
                disabled={(date) => date < (checkIn || new Date())}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Guests */}
        <div className="space-y-2">
          <Label className="text-slate-600 text-sm flex items-center gap-1">
            <Users className="w-4 h-4" />
            Guests
          </Label>
          <Input
            type="number"
            min="1"
            max="20"
            placeholder="Add guests"
            value={guests}
            onChange={(e) => setGuests(e.target.value)}
            className="input-default"
            data-testid="search-guests"
          />
        </div>

        {/* Search Button */}
        {isHero && (
          <div className="flex items-end">
            <Button 
              onClick={handleSearch}
              className="btn-accent w-full h-12"
              data-testid="search-submit"
            >
              <Search className="w-5 h-5 mr-2" />
              Search
            </Button>
          </div>
        )}
      </div>

      {!isHero && (
        <div className="mt-4">
          <Button 
            onClick={handleSearch}
            className="btn-primary w-full"
            data-testid="search-submit"
          >
            <Search className="w-5 h-5 mr-2" />
            Search Properties
          </Button>
        </div>
      )}
    </div>
  );
}
